import { FC, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Tag, RefreshCw, Search, Settings, AlertTriangle, TrendingUp,
  TrendingDown, ShoppingCart, X, Check, ChevronDown,
  ChevronUp, Package, Zap, BarChart3, ArrowUpRight, ArrowDownRight,
  CloudDownload, CheckCircle, Pencil, Layers, Bot,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { IStoreResponse } from '../interfaces/responses/StoreResponse.interface';
import AutoPilotPanel from '../components/pricing/AutoPilotPanel';
import './PricingIntelligence.page.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PricingSkuData {
  _id: string;
  sku: string;
  productUrl?: string | null;
  productName: string;
  productGroupId: string;
  productGroupName: string;
  productImage: string;
  storeId: string;
  storeName: string;
  isProtected: boolean;

  // Costs
  buyingCost: number;
  newMarketPrice: number;
  transportFees: number;
  storageFees: number;
  packageFees: number;
  fraisTraitement: number;
  commissionPct: number;
  totalFixedCosts: number;

  // Current metrics
  currentPrice: number | null;
  currentCommission: number | null;
  currentTotalCost: number | null;
  currentProfit: number | null;
  currentMarginPct: number | null;

  // Order metrics
  lastOrderDate: string | null;
  orders7d: number;
  orders14d: number;
  orders30d: number;
  delivered7d: number;
  delivered30d: number;
  daysWithoutOrder: number;

  // Recommendation
  floorPrice: number;
  suggestedPrice: number | null;
  suggestedProfit: number | null;
  suggestedMarginPct: number | null;
  priceChangeDiff: number | null;

  // Intelligence
  reason: string;
  opportunityType: 'high-price' | 'no-orders' | 'thin-margin' | 'reprice-opportunity' | 'missing-price' | 'healthy';
  riskLevel: 'safe' | 'risky' | 'missing-data';
  missingCost: boolean;
  missingPrice: boolean;
  badges: string[];
  priceSource: 'cache' | 'order-estimated' | null;
  priceCachedAt: string | null;
  manualStock: number | null;
  jumiaQuantity: number | null;
  jumiaQuantitySyncedAt: string | null;
  visible: number; // 1 = visible on the site, 0 = hidden (stock 0 / not visible), -1 = unknown
  listingStatus?: string | null;   // ACTIVE / INACTIVE / DELETED … null = unknown
  qcStatus?: string | null;        // APPROVED / REJECTED / PENDING …
  visibleUpdatedAt?: string | null;
}

interface SyncStatus {
  running: boolean;
  done: number;
  errors: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
  lastSyncedAt: string | null;
  oldestPrice: string | null;
  total: number;
  fresh24h: number;
  fresh6h: number;
  errored: number;
  batchSize: number;
}

interface StockSyncStatus {
  running: boolean;
  done: number;
  errors: number;
  total: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
}

interface PricingSettings {
  minProfitAmount: number;
  minMarginPct: number;
  daysWithoutOrderThreshold: number;
  psychPricingPool: number[];
  autoExecutionEnabled: boolean;
  maxAllowedPriceDrop: number;
  executionMode: 'suggestion' | 'approval' | 'auto';
}

interface PricingLog {
  _id: string;
  sku: string;
  oldPrice: number | null;
  newPrice: number;
  profitBefore: number | null;
  profitAfter: number | null;
  marginBefore: number | null;
  marginAfter: number | null;
  reason: string | null;
  mode: string;
  status: string;
  errorMessage: string | null;
  createdAt: string;
}

type TabKey = 'all' | 'no-orders' | 'high-price' | 'thin-margin' | 'reprice' | 'healthy' | 'groups' | 'autopilot' | 'logs';

// ─── Constants ────────────────────────────────────────────────────────────────
const DEFAULT_PSYCH_POOL = [
  99, 119, 129, 139, 149, 159, 169, 179, 189, 199,
  219, 229, 249, 279, 299, 349, 399, 449, 499,
  549, 599, 649, 699, 749, 799, 849, 899, 949, 999,
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD  = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('fr-MA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} MAD`;
const fmtPct  = (n: number | null | undefined) => n == null ? '—' : `${n.toFixed(1)}%`;
const clamp   = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const margCol = (m: number | null) =>
  m == null ? '' : m >= 20 ? 'pi__num--green' : m >= 8 ? 'pi__num--amber' : 'pi__num--red';

const daysClass = (d: number) =>
  d >= 14 ? 'pi__days--critical' : d >= 7 ? 'pi__days--warning' : 'pi__days--ok';

const snapToPsych = (price: number, pool: number[]): number => {
  if (!price || price <= 0) return pool[0];
  const found = pool.find(p => p >= price);
  if (found !== undefined) return found;
  let last = pool[pool.length - 1];
  while (last < price) last += 50;
  return last;
};

const badgeClass = (badge: string) => {
  if (badge === 'No Orders')    return 'pi__badge--no-orders';
  if (badge === 'High Price')   return 'pi__badge--high-price';
  if (badge === 'Thin Margin')  return 'pi__badge--thin-margin';
  if (badge === 'Reprice Opp')  return 'pi__badge--reprice-opp';
  if (badge === 'Safe Reprice') return 'pi__badge--safe';
  if (badge === 'Risky')        return 'pi__badge--risky';
  if (badge === 'Missing Cost' || badge === 'Missing Price') return 'pi__badge--missing';
  return 'pi__badge--missing';
};

// ─── Sort header ──────────────────────────────────────────────────────────────
const SortTh: FC<{
  field: string; label: string;
  sortBy: string; sortDir: 'asc' | 'desc';
  onSort: (f: string) => void;
  accent?: string;
}> = ({ field, label, sortBy, sortDir, onSort, accent }) => (
  <th
    className={`sortable${sortBy === field ? ' sorted' : ''}${accent ? ' ' + accent : ''}`}
    onClick={() => onSort(field)}
  >
    {label}{sortBy === field ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
  </th>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────
interface ConfirmModalProps {
  items: Array<{
    sku: PricingSkuData;
    finalPrice: number;
  }>;
  onClose: () => void;
  onConfirm: () => void;
  applying: boolean;
  error: string | null;
}

const ConfirmModal: FC<ConfirmModalProps> = ({ items, onClose, onConfirm, applying, error }) => {
  const isLarge = items.length > 1;
  const uniqueGroups = Array.from(new Set(items.map(i => i.sku.productGroupName || String(i.sku.productGroupId))));
  const multiGroup   = uniqueGroups.length > 1;
  return (
    <div className="pi__modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pi__modal">
        <div className="pi__modal-header">
          <div className="pi__modal-title">
            {isLarge
              ? `Confirm Bulk Reprice — ${items.length} SKUs`
              : `Confirm Price Change`}
          </div>
          <button className="pi__modal-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="pi__modal-body">
          {multiGroup && (
            <div className="pi__warning-box" style={{ marginBottom: '0.75rem' }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                These {items.length} SKUs belong to <strong>{uniqueGroups.length} different product groups</strong>.
                Confirm that this bulk price change is intentional across all groups.
              </span>
            </div>
          )}
          {items.slice(0, 8).map(({ sku, finalPrice }) => {
            const diff = sku.currentPrice ? finalPrice - sku.currentPrice : null;
            const commAmt = finalPrice * ((sku.commissionPct || 15) / 100);
            const newProfit = finalPrice - sku.totalFixedCosts - commAmt;
            const newMargin = finalPrice > 0 ? (newProfit / finalPrice) * 100 : 0;
            const isBelowFloor = finalPrice < sku.floorPrice;
            const isLargeDropPct = sku.currentPrice && diff != null
              ? Math.abs(diff / sku.currentPrice) * 100 > 20
              : false;

            return (
              <div key={sku._id} className="pi__confirm-table">
                {isLarge && (
                  <div className="pi__confirm-row pi__confirm-row--header">
                    <span className="pi__confirm-label">{sku.sku} — {sku.productGroupName}</span>
                  </div>
                )}

                {!isLarge && (
                  <div className="pi__confirm-row pi__confirm-row--header">
                    <span>SKU</span>
                    <span style={{ fontWeight: 600, color: '#1A1D23' }}>{sku.sku}</span>
                  </div>
                )}

                <div className="pi__confirm-row">
                  <span className="pi__confirm-label">Old price</span>
                  <span className="pi__confirm-old">{fmtMAD(sku.currentPrice)}</span>
                  <span className="pi__confirm-new">{fmtMAD(finalPrice)}</span>
                  {diff != null && (
                    <span className={diff < 0 ? 'pi__confirm-diff--down' : 'pi__confirm-diff--up'}>
                      {diff > 0 ? '+' : ''}{Math.round(diff)} MAD
                    </span>
                  )}
                </div>

                <div className="pi__confirm-row">
                  <span className="pi__confirm-label">Est. profit after</span>
                  <span className="pi__confirm-old" style={{ textDecoration: 'none' }}>
                    {fmtMAD(sku.currentProfit)}
                  </span>
                  <span className={`pi__confirm-new${newProfit < 0 ? ' pi__num--red' : ''}`}>
                    {fmtMAD(newProfit)}
                  </span>
                </div>

                <div className="pi__confirm-row">
                  <span className="pi__confirm-label">Margin after</span>
                  <span className="pi__confirm-old" style={{ textDecoration: 'none' }}>
                    {fmtPct(sku.currentMarginPct)}
                  </span>
                  <span className={`pi__confirm-new${newMargin < 0 ? ' pi__num--red' : ''}`}>
                    {fmtPct(newMargin)}
                  </span>
                </div>

                {isBelowFloor && (
                  <div style={{ padding: '0.5rem 0.875rem' }}>
                    <div className="pi__warning-box">
                      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      Price is below floor ({fmtMAD(sku.floorPrice)}). This will result in a loss or zero profit.
                    </div>
                  </div>
                )}
                {isLargeDropPct && !isBelowFloor && (
                  <div style={{ padding: '0.5rem 0.875rem' }}>
                    <div className="pi__warning-box">
                      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      Large price drop detected (&gt;20%). Make sure this is intentional.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {items.length > 8 && (
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center' }}>
              …and {items.length - 8} more SKUs
            </div>
          )}

          {error && <div className="pi__error-box">⚠ {error}</div>}
        </div>

        <div className="pi__modal-footer">
          <button className="pi__action-btn pi__action-btn--ghost" onClick={onClose} disabled={applying}>
            Cancel
          </button>
          <button className="pi__action-btn pi__action-btn--apply" onClick={onConfirm} disabled={applying}>
            {applying
              ? <><RefreshCw size={12} className="pi__refresh-spin" /> Applying…</>
              : <><Check size={12} /> Apply {items.length > 1 ? `${items.length} Prices` : 'Price'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Apply Modal (single SKU with custom price / psych picker) ─────────────────
interface ApplyModalProps {
  sku: PricingSkuData;
  psychPool: number[];
  onClose: () => void;
  onApply: (sku: PricingSkuData, price: number) => void;
}

const ApplyModal: FC<ApplyModalProps> = ({ sku, psychPool, onClose, onApply }) => {
  const [customPrice, setCustomPrice] = useState(String(sku.suggestedPrice ?? sku.currentPrice ?? ''));
  const [selected, setSelected]       = useState<number | null>(sku.suggestedPrice ?? null);

  const finalPrice = selected ?? (parseFloat(customPrice) || 0);
  const commRate   = (sku.commissionPct || 15) / 100;
  const commAmt    = finalPrice * commRate;
  const newProfit  = finalPrice > 0 ? finalPrice - sku.totalFixedCosts - commAmt : null;
  const newMargin  = finalPrice > 0 && newProfit != null ? (newProfit / finalPrice) * 100 : null;
  const isBelowFloor = finalPrice > 0 && finalPrice < sku.floorPrice;

  const hasMarketPrice = sku.newMarketPrice > 0;
  const competitorFixedCosts = hasMarketPrice
    ? sku.newMarketPrice + sku.transportFees + sku.storageFees + sku.packageFees + sku.fraisTraitement
    : null;
  const competitorFloor = competitorFixedCosts != null
    ? Math.ceil(competitorFixedCosts / (1 - commRate))
    : null;
  const competitorProfit = (finalPrice > 0 && competitorFixedCosts != null)
    ? finalPrice - competitorFixedCosts - commAmt
    : null;
  const competitorMargin = (finalPrice > 0 && competitorProfit != null)
    ? (competitorProfit / finalPrice) * 100
    : null;

  const nearbyPool = useMemo(() => {
    const base = sku.currentPrice || sku.floorPrice;
    return psychPool.filter(p => p >= sku.floorPrice * 0.8 && p <= base * 1.4);
  }, [psychPool, sku.currentPrice, sku.floorPrice]);

  return (
    <div className="pi__modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pi__modal pi__modal--wide">

        {/* ── Header ── */}
        <div className="pi__modal-header">
          <div>
            <div className="pi__modal-title">Set Price — {sku.sku}</div>
            <div style={{ fontSize: '0.72rem', color: '#6B7280', marginTop: 2 }}>
              {sku.productGroupName} {sku.currentPrice ? `· Current: ${sku.currentPrice} MAD` : ''}
            </div>
          </div>
          <button className="pi__modal-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="pi__modal-body">

          {/* ── Row 1: Costs + Floors ── */}
          <div className="pi__modal-top-grid">

            {/* Cost breakdown */}
            <div className="pi__info-card">
              <div className="pi__info-card-title">Cost Breakdown</div>
              {[
                ['Buying cost',          sku.buyingCost],
                ['Inbound transport',    sku.transportFees],
                ['Hub delivery cost',    sku.storageFees],
                ['Packaging',            sku.packageFees],
                ['Frais de traitement',  sku.fraisTraitement],
              ].map(([label, val]) => (
                <div key={String(label)} className="pi__info-row">
                  <span className="pi__info-label">{label}</span>
                  <span className="pi__info-val">{fmtMAD(val as number)}</span>
                </div>
              ))}
              <div className="pi__info-row pi__info-row--total">
                <span className="pi__info-label">Commission ({sku.commissionPct}%)</span>
                <span className="pi__info-val">{finalPrice > 0 ? fmtMAD(commAmt) : '—'}</span>
              </div>
            </div>

            {/* Floor prices */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <div className="pi__floor-card pi__floor-card--ours">
                <div className="pi__floor-card-label">Our floor (0 profit)</div>
                <div className="pi__floor-card-value">{sku.floorPrice} MAD</div>
              </div>
              {competitorFloor != null && (
                <div className="pi__floor-card pi__floor-card--market">
                  <div className="pi__floor-card-label">New Cost Basis Floor</div>
                  <div className="pi__floor-card-value">{competitorFloor} MAD</div>
                  <div className="pi__floor-card-sub">based on latest purchase price</div>
                </div>
              )}
              {sku.currentPrice != null && (
                <div className="pi__floor-card" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <div className="pi__floor-card-label" style={{ color: '#6B7280' }}>Current price</div>
                  <div className="pi__floor-card-value" style={{ color: '#374151' }}>{sku.currentPrice} MAD</div>
                </div>
              )}
            </div>
          </div>

          {/* ── Price picker ── */}
          <div>
            <div className="pi__section-label">Psychological Prices</div>
            <div className="pi__psych-grid">
              {nearbyPool.map(p => (
                <button key={p}
                  className={`pi__psych-chip${selected === p ? ' pi__psych-chip--active' : ''}${p < sku.floorPrice ? ' pi__psych-chip--below-floor' : ''}`}
                  onClick={() => { setSelected(p); setCustomPrice(String(p)); }}
                  title={p < sku.floorPrice ? `Below floor (${sku.floorPrice} MAD)` : undefined}
                >{p}</button>
              ))}
            </div>
          </div>

          {/* ── Custom input ── */}
          <div>
            <div className="pi__section-label">Custom Price (MAD)</div>
            <div className="pi__custom-price-row">
              <input type="number" min={1} step={1} autoFocus
                className="pi__price-input"
                value={customPrice}
                onChange={e => { setCustomPrice(e.target.value); setSelected(null); }}
                onFocus={() => setSelected(null)}
                placeholder="e.g. 149"
              />
              {parseFloat(customPrice) > 0 && (
                <button className="pi__psych-chip" style={{ whiteSpace: 'nowrap' }}
                  onClick={() => { const s = snapToPsych(parseFloat(customPrice), psychPool); setCustomPrice(String(s)); setSelected(s); }}>
                  → Snap to {snapToPsych(parseFloat(customPrice), psychPool)}
                </button>
              )}
            </div>
          </div>

          {/* ── Live preview cards ── */}
          {finalPrice > 0 && (
            <div>
              <div className="pi__section-label">Preview at {finalPrice} MAD</div>
              <div className={`pi__preview-grid${competitorProfit != null ? ' pi__preview-grid--two' : ''}`}>

                {/* Our performance */}
                <div className="pi__perf-card pi__perf-card--ours">
                  <div className="pi__perf-card-title">Our Performance</div>
                  <div className="pi__perf-row">
                    <span>Est. profit</span>
                    <strong style={{ color: newProfit != null && newProfit < 0 ? '#DC2626' : '#1D9E75' }}>
                      {fmtMAD(newProfit)}
                    </strong>
                  </div>
                  <div className="pi__perf-row">
                    <span>Est. margin</span>
                    <strong style={{ color: newMargin != null && newMargin < 0 ? '#DC2626' : '#1D9E75' }}>
                      {fmtPct(newMargin)}
                    </strong>
                  </div>
                </div>

                {/* Next batch projection */}
                {competitorProfit != null && (
                  <div className="pi__perf-card pi__perf-card--comp">
                    <div className="pi__perf-card-title">Next Batch Est. (new rate)</div>
                    <div className="pi__perf-row">
                      <span>Est. profit</span>
                      <strong style={{ color: competitorProfit < 0 ? '#DC2626' : '#EA580C' }}>
                        {fmtMAD(competitorProfit)}
                      </strong>
                    </div>
                    <div className="pi__perf-row">
                      <span>Est. margin</span>
                      <strong style={{ color: competitorMargin != null && competitorMargin < 0 ? '#DC2626' : '#EA580C' }}>
                        {fmtPct(competitorMargin)}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {isBelowFloor && (
            <div className="pi__warning-box">
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              {finalPrice} MAD is below your floor ({sku.floorPrice} MAD) — every sale will be a loss.
            </div>
          )}
        </div>

        <div className="pi__modal-footer">
          <button className="pi__action-btn pi__action-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pi__action-btn pi__action-btn--apply"
            disabled={!finalPrice || finalPrice <= 0}
            onClick={() => finalPrice > 0 && onApply(sku, finalPrice)}>
            <Check size={12} /> Apply {finalPrice > 0 ? `${finalPrice} MAD` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Bulk Custom Price Modal ──────────────────────────────────────────────────
interface BulkCustomPriceModalProps {
  selectedSkus: PricingSkuData[];
  psychPool: number[];
  onClose: () => void;
  onApply: (price: number) => void;
}

const BulkCustomPriceModal: FC<BulkCustomPriceModalProps> = ({ selectedSkus, psychPool, onClose, onApply }) => {
  const [price,    setPrice]    = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  const finalPrice = selected ?? (parseFloat(price) || 0);
  const maxFloor   = Math.max(...selectedSkus.map(s => s.floorPrice || 0));

  const avgCommPct   = selectedSkus.reduce((s, x) => s + (x.commissionPct || 15), 0) / selectedSkus.length;
  const avgCommRate  = avgCommPct / 100;
  const avgFixedCost = selectedSkus.reduce((s, x) => s + (x.totalFixedCosts || 0), 0) / selectedSkus.length;
  const avgCommAmt   = finalPrice * avgCommRate;

  const estProfit = finalPrice > 0 ? finalPrice - avgCommAmt - avgFixedCost : null;
  const estMargin = finalPrice > 0 && estProfit != null ? (estProfit / finalPrice) * 100 : null;

  // Competitor averages (only for SKUs that have newMarketPrice set)
  const skusWithMarket = selectedSkus.filter(s => s.newMarketPrice > 0);
  const hasCompetitor  = skusWithMarket.length > 0;
  const avgMarketFixedCost = hasCompetitor
    ? skusWithMarket.reduce((s, x) =>
        s + x.newMarketPrice + x.transportFees + x.storageFees + x.packageFees + x.fraisTraitement, 0
      ) / skusWithMarket.length
    : null;
  const avgMarketFloor = avgMarketFixedCost != null
    ? Math.ceil(avgMarketFixedCost / (1 - avgCommRate))
    : null;
  const compProfit = (finalPrice > 0 && avgMarketFixedCost != null)
    ? finalPrice - avgMarketFixedCost - avgCommAmt
    : null;
  const compMargin = (finalPrice > 0 && compProfit != null)
    ? (compProfit / finalPrice) * 100
    : null;

  const belowFloorCount = finalPrice > 0
    ? selectedSkus.filter(s => finalPrice < s.floorPrice).length
    : 0;

  const avgCurrentPrice = selectedSkus.filter(s => s.currentPrice).reduce(
    (sum, s, _, arr) => sum + (s.currentPrice! / arr.length), 0
  );
  const nearbyPool = psychPool.filter(p => p >= maxFloor * 0.8 && p <= Math.max(avgCurrentPrice, maxFloor) * 1.5);

  return (
    <div className="pi__modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pi__modal pi__modal--wide">
        <div className="pi__modal-header">
          <div>
            <div className="pi__modal-title">Set Custom Price — {selectedSkus.length} SKUs</div>
            <div style={{ fontSize: '0.72rem', color: '#6B7280', marginTop: 2 }}>
              Highest floor: <strong>{maxFloor} MAD</strong> · stay above to remain profitable
            </div>
          </div>
          <button className="pi__modal-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="pi__modal-body">

          {/* SKU chips */}
          <div style={{ background: '#F9FAFB', borderRadius: 8, border: '0.5px solid #E2E4E9', padding: '0.75rem 1rem' }}>
            <div className="pi__section-label" style={{ marginBottom: '0.5rem' }}>Selected SKUs</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', maxHeight: 80, overflowY: 'auto' }}>
              {selectedSkus.map(s => (
                <span key={s._id} style={{
                  fontSize: '0.72rem', background: '#fff', border: '0.5px solid #E2E4E9',
                  borderRadius: 4, padding: '0.15rem 0.5rem', color: '#374151',
                }}>
                  {s.sku}{s.currentPrice ? ` · ${s.currentPrice}` : ''}
                </span>
              ))}
            </div>
          </div>

          {/* Floor reference row */}
          <div style={{ display: 'flex', gap: '0.625rem' }}>
            <div className="pi__floor-card pi__floor-card--ours" style={{ flex: 1 }}>
              <div className="pi__floor-card-label">Highest floor (our cost)</div>
              <div className="pi__floor-card-value">{maxFloor} MAD</div>
            </div>
            {avgMarketFloor != null && (
              <div className="pi__floor-card pi__floor-card--market" style={{ flex: 1 }}>
                <div className="pi__floor-card-label">New Cost Basis Floor ({skusWithMarket.length} SKUs)</div>
                <div className="pi__floor-card-value">{avgMarketFloor} MAD</div>
              </div>
            )}
          </div>

          {/* Psych chips */}
          <div>
            <div className="pi__section-label">Psychological Prices</div>
            <div className="pi__psych-grid">
              {nearbyPool.map(p => (
                <button key={p}
                  className={`pi__psych-chip${selected === p ? ' pi__psych-chip--active' : ''}${p < maxFloor ? ' pi__psych-chip--below-floor' : ''}`}
                  onClick={() => { setSelected(p); setPrice(String(p)); }}
                  title={p < maxFloor ? `Below highest floor (${maxFloor} MAD)` : undefined}
                >{p}</button>
              ))}
            </div>
          </div>

          {/* Custom input */}
          <div>
            <div className="pi__section-label">Custom Price (MAD)</div>
            <div className="pi__custom-price-row">
              <input type="number" min={1} step={1} autoFocus
                className="pi__price-input"
                placeholder={`e.g. ${maxFloor + 20}`}
                value={price}
                onChange={e => { setPrice(e.target.value); setSelected(null); }}
                onFocus={() => setSelected(null)}
              />
              {parseFloat(price) > 0 && (
                <button className="pi__psych-chip" style={{ whiteSpace: 'nowrap' }}
                  onClick={() => { const s = snapToPsych(parseFloat(price), psychPool); setPrice(String(s)); setSelected(s); }}>
                  → Snap to {snapToPsych(parseFloat(price), psychPool)}
                </button>
              )}
            </div>
          </div>

          {/* Live preview */}
          {finalPrice > 0 && (
            <div>
              <div className="pi__section-label">Preview at {finalPrice} MAD (avg across selection)</div>
              <div className={`pi__preview-grid${compProfit != null ? ' pi__preview-grid--two' : ''}`}>
                <div className="pi__perf-card pi__perf-card--ours">
                  <div className="pi__perf-card-title">Our Performance (avg)</div>
                  <div className="pi__perf-row">
                    <span>Est. profit / unit</span>
                    <strong style={{ color: estProfit != null && estProfit < 0 ? '#DC2626' : '#1D9E75' }}>
                      {estProfit != null ? `${estProfit.toFixed(1)} MAD` : '—'}
                    </strong>
                  </div>
                  <div className="pi__perf-row">
                    <span>Est. margin</span>
                    <strong style={{ color: estMargin != null && estMargin < 0 ? '#DC2626' : '#1D9E75' }}>
                      {estMargin != null ? `${estMargin.toFixed(1)}%` : '—'}
                    </strong>
                  </div>
                </div>
                {compProfit != null && (
                  <div className="pi__perf-card pi__perf-card--comp">
                    <div className="pi__perf-card-title">Next Batch Est. ({skusWithMarket.length} SKUs)</div>
                    <div className="pi__perf-row">
                      <span>Est. profit / unit</span>
                      <strong style={{ color: compProfit < 0 ? '#DC2626' : '#EA580C' }}>
                        {compProfit.toFixed(1)} MAD
                      </strong>
                    </div>
                    <div className="pi__perf-row">
                      <span>Est. margin</span>
                      <strong style={{ color: compMargin != null && compMargin < 0 ? '#DC2626' : '#EA580C' }}>
                        {compMargin != null ? `${compMargin.toFixed(1)}%` : '—'}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {belowFloorCount > 0 && (
            <div className="pi__warning-box">
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              {belowFloorCount} SKU{belowFloorCount > 1 ? 's' : ''} have a floor above {finalPrice} MAD — those will be sold at a loss.
            </div>
          )}
        </div>

        <div className="pi__modal-footer">
          <button className="pi__action-btn pi__action-btn--ghost" onClick={onClose}>Cancel</button>
          <button
            className="pi__action-btn pi__action-btn--apply"
            disabled={!finalPrice || finalPrice <= 0}
            onClick={() => finalPrice > 0 && onApply(finalPrice)}
          >
            <Check size={12} /> Apply {finalPrice > 0 ? `${finalPrice} MAD` : ''} to {selectedSkus.length} SKUs
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Stock Confirm Modal ──────────────────────────────────────────────────────
interface StockConfirmModalProps {
  skus: PricingSkuData[];
  quantity: number;
  onClose: () => void;
  onConfirm: () => void;
  applying: boolean;
  error: string | null;
}

const StockConfirmModal: FC<StockConfirmModalProps> = ({ skus, quantity, onClose, onConfirm, applying, error }) => {
  const uniqueGroups = Array.from(new Set(skus.map(s => s.productGroupName || String(s.productGroupId))));
  const multiGroup   = uniqueGroups.length > 1;
  return (
  <div className="pi__modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="pi__modal">
      <div className="pi__modal-header">
        <div className="pi__modal-title">
          {skus.length === 1 ? `Set Stock — ${skus[0].sku}` : `Bulk Set Stock — ${skus.length} SKUs`}
        </div>
        <button className="pi__modal-close" onClick={onClose}><X size={14} /></button>
      </div>
      <div className="pi__modal-body">
        {multiGroup && (
          <div className="pi__warning-box" style={{ marginBottom: '0.75rem' }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              These {skus.length} SKUs belong to <strong>{uniqueGroups.length} different product groups</strong>.
              Confirm that this bulk stock change is intentional across all groups.
            </span>
          </div>
        )}
        <div style={{ padding: '0.75rem 0' }}>
          <div style={{ fontSize: '0.82rem', color: '#374151', marginBottom: '0.5rem' }}>
            Set <strong>Allocated Stock = {quantity}</strong> on Jumia for:
          </div>
          {skus.slice(0, 6).map(s => (
            <div key={s._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '0.5px solid #F3F4F6', fontSize: '0.78rem' }}>
              <span style={{ color: '#374151', fontWeight: 500 }}>{s.sku}</span>
              <span style={{ color: '#9CA3AF' }}>
                {s.jumiaQuantity != null ? `${s.jumiaQuantity} → ${quantity}` : `→ ${quantity}`}
              </span>
            </div>
          ))}
          {skus.length > 6 && (
            <div style={{ fontSize: '0.72rem', color: '#9CA3AF', textAlign: 'center', marginTop: '0.35rem' }}>
              …and {skus.length - 6} more SKUs
            </div>
          )}
        </div>
        {quantity === 0 && (
          <div className="pi__warning-box">
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            Setting stock to 0 will hide the product from Jumia search until stock is restored.
          </div>
        )}
        {error && <div className="pi__error-box">⚠ {error}</div>}
      </div>
      <div className="pi__modal-footer">
        <button className="pi__action-btn pi__action-btn--ghost" onClick={onClose} disabled={applying}>Cancel</button>
        <button className="pi__action-btn pi__action-btn--apply" onClick={onConfirm} disabled={applying}>
          {applying
            ? <><RefreshCw size={12} className="pi__refresh-spin" /> Applying…</>
            : <><Check size={12} /> Confirm {skus.length > 1 ? `${skus.length} SKUs` : ''}</>}
        </button>
      </div>
    </div>
  </div>
  );
};

// ─── Settings Panel ───────────────────────────────────────────────────────────
interface SettingsPanelProps {
  settings: PricingSettings;
  onClose: () => void;
  onSave: (s: PricingSettings) => void;
  saving: boolean;
}

const SettingsPanel: FC<SettingsPanelProps> = ({ settings, onClose, onSave, saving }) => {
  const [local, setLocal] = useState<PricingSettings>({ ...settings });

  const upd = (key: keyof PricingSettings, value: any) =>
    setLocal(prev => ({ ...prev, [key]: value }));

  return (
    <>
      <div className="pi__settings-overlay" onClick={onClose} />
      <div className="pi__settings-panel">
        <div className="pi__settings-header">
          <span className="pi__settings-title">Pricing Settings</span>
          <button className="pi__modal-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="pi__settings-body">
          <div className="pi__settings-section">
            <div className="pi__settings-section-title">Profit Floor</div>
            <div className="pi__settings-row">
              <div>
                <div className="pi__settings-label">Min profit per unit (MAD)</div>
                <div className="pi__settings-sublabel">Floor = all costs + this amount</div>
              </div>
              <input
                type="number" min={0} step={1}
                className="pi__settings-input"
                value={local.minProfitAmount}
                onChange={e => upd('minProfitAmount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="pi__settings-row">
              <div>
                <div className="pi__settings-label">Min margin % target</div>
                <div className="pi__settings-sublabel">For display classification only</div>
              </div>
              <input
                type="number" min={0} max={100} step={1}
                className="pi__settings-input"
                value={local.minMarginPct}
                onChange={e => upd('minMarginPct', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="pi__settings-section">
            <div className="pi__settings-section-title">No-Orders Detection</div>
            <div className="pi__settings-row">
              <div>
                <div className="pi__settings-label">Days without order threshold</div>
                <div className="pi__settings-sublabel">Flag SKUs with 0 orders for this many days</div>
              </div>
              <input
                type="number" min={1} step={1}
                className="pi__settings-input"
                value={local.daysWithoutOrderThreshold}
                onChange={e => upd('daysWithoutOrderThreshold', parseInt(e.target.value) || 7)}
              />
            </div>
          </div>

          <div className="pi__settings-section">
            <div className="pi__settings-section-title">Safety Limits</div>
            <div className="pi__settings-row">
              <div>
                <div className="pi__settings-label">Max allowed price drop (%)</div>
                <div className="pi__settings-sublabel">Warn when a suggested drop exceeds this</div>
              </div>
              <input
                type="number" min={1} max={80} step={1}
                className="pi__settings-input"
                value={local.maxAllowedPriceDrop}
                onChange={e => upd('maxAllowedPriceDrop', parseInt(e.target.value) || 20)}
              />
            </div>
          </div>

          <div className="pi__settings-section">
            <div className="pi__settings-section-title">Execution Mode</div>
            <div className="pi__settings-row">
              <div className="pi__settings-label">Mode</div>
              <select
                className="pi__filter-select"
                value={local.executionMode}
                onChange={e => upd('executionMode', e.target.value)}
              >
                <option value="suggestion">Suggestion only (no auto-apply)</option>
                <option value="approval">Approval required</option>
              </select>
            </div>
          </div>

          <div className="pi__settings-section">
            <div className="pi__settings-section-title">Psychological Prices</div>
            <div style={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.6 }}>
              Current pool ({local.psychPricingPool.length} prices):
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.35rem' }}>
                {local.psychPricingPool.slice(0, 20).map(p => (
                  <span key={p} className="pi__psych-chip" style={{ cursor: 'default', padding: '0.1rem 0.45rem', fontSize: '0.68rem' }}>
                    {p}
                  </span>
                ))}
                {local.psychPricingPool.length > 20 && (
                  <span style={{ fontSize: '0.68rem', color: '#9CA3AF', alignSelf: 'center' }}>
                    +{local.psychPricingPool.length - 20} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="pi__settings-footer">
          <button className="pi__action-btn pi__action-btn--ghost" style={{ flex: 1 }} onClick={onClose}>
            Cancel
          </button>
          <button className="pi__save-btn" onClick={() => onSave(local)} disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Logs Table ───────────────────────────────────────────────────────────────
const LogsTable: FC<{ token: string }> = ({ token }) => {
  const [logs,    setLogs]    = useState<PricingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [total,   setTotal]   = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await dispatch(actions.getPricingLogs, {}, token, { limit: 50, page: 0 });
      if (data && !data.error) {
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
      setLoading(false);
    })();
  }, [token]);

  if (loading) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.82rem' }}>
      Loading logs…
    </div>
  );

  if (logs.length === 0) return (
    <div className="pi__empty">
      <div className="pi__empty-icon">📋</div>
      <div className="pi__empty-msg">No repricing logs yet. Apply your first price change to see it here.</div>
    </div>
  );

  return (
    <div className="pi__logs-wrap">
      <table className="pi__table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Old Price</th>
            <th>New Price</th>
            <th>Profit Before → After</th>
            <th>Reason</th>
            <th>Mode</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log._id} className="pi__row">
              <td><span className="pi__num">{log.sku}</span></td>
              <td><span className="pi__num--muted">{fmtMAD(log.oldPrice)}</span></td>
              <td><span className="pi__num">{fmtMAD(log.newPrice)}</span></td>
              <td>
                <span className="pi__num--muted">{fmtMAD(log.profitBefore)}</span>
                {' → '}
                <span className={log.profitAfter != null && log.profitAfter > 0 ? 'pi__num--green' : 'pi__num--red'}>
                  {fmtMAD(log.profitAfter)}
                </span>
              </td>
              <td>
                <span style={{ fontSize: '0.72rem', color: '#6B7280', maxWidth: 220, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.reason || '—'}
                </span>
              </td>
              <td>
                <span style={{ fontSize: '0.72rem', color: '#6B7280' }}>{log.mode}</span>
              </td>
              <td>
                <span className={`pi__log-status pi__log-status--${log.status}`}>
                  {log.status}
                </span>
              </td>
              <td>
                <span style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                  {new Date(log.createdAt).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > logs.length && (
        <div style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center', borderTop: '0.5px solid #F3F4F6' }}>
          Showing {logs.length} of {total} entries
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PricingIntelligencePage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;

  // Data
  const [stores,  setStores]  = useState<IStoreResponse[]>([]);
  const [skus,    setSkus]    = useState<PricingSkuData[]>([]);
  const [loading, setLoading] = useState(true);
  const [storeId, setStoreId] = useState('0');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Settings
  const [settings, setSettings] = useState<PricingSettings>({
    minProfitAmount: 0, minMarginPct: 0,
    daysWithoutOrderThreshold: 7,
    psychPricingPool: DEFAULT_PSYCH_POOL,
    autoExecutionEnabled: false,
    maxAllowedPriceDrop: 20,
    executionMode: 'suggestion',
  });
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // UI state
  const [activeTab,      setActiveTab]      = useState<TabKey>('all');
  const [search,         setSearch]         = useState('');
  const [sortBy,         setSortBy]         = useState('daysWithout');
  const [sortDir,        setSortDir]        = useState<'asc' | 'desc'>('desc');
  const [filterGroup,    setFilterGroup]    = useState('all');
  const [filterRisk,     setFilterRisk]     = useState('all');
  const [filterVisible,  setFilterVisible]  = useState('all');
  const [page,           setPage]           = useState(0);
  const PAGE_SIZE = 100;

  // Selection & apply
  const [selected,          setSelected]          = useState<Set<string>>(new Set());
  const [applyTarget,       setApplyTarget]       = useState<PricingSkuData | null>(null);
  const [confirmItems,      setConfirmItems]      = useState<Array<{ sku: PricingSkuData; finalPrice: number }> | null>(null);
  const [applying,          setApplying]          = useState(false);
  const [applyError,        setApplyError]        = useState<string | null>(null);
  const [showBulkCustom,    setShowBulkCustom]    = useState(false);
  const [inlineEdit,        setInlineEdit]        = useState<{ id: string; price: string } | null>(null);
  const [successFlash,   setSuccessFlash]   = useState<string | null>(null);

  // Inline live price fetching (single-SKU fallback)
  const [fetchingPrice,  setFetchingPrice]  = useState<Set<string>>(new Set());
  const [livePrices,     setLivePrices]     = useState<Record<string, number>>({});

  // Bulk price sync state
  const [syncStatus,     setSyncStatus]     = useState<SyncStatus | null>(null);
  const [syncing,        setSyncing]        = useState(false);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stock state
  const [liveStock,          setLiveStock]          = useState<Record<string, number>>({});
  const [inlineStockEdit,    setInlineStockEdit]    = useState<{ id: string; qty: string } | null>(null);
  const [stockConfirmItems,  setStockConfirmItems]  = useState<{ skus: PricingSkuData[]; quantity: number } | null>(null);
  const [applyingStock,      setApplyingStock]      = useState(false);
  const [stockError,         setStockError]         = useState<string | null>(null);
  const [stockSyncStatus,    setStockSyncStatus]    = useState<StockSyncStatus | null>(null);
  const [syncingStock,       setSyncingStock]       = useState(false);
  const [showBulkStock,      setShowBulkStock]      = useState(false);
  const [bulkStockQty,       setBulkStockQty]       = useState('');
  const stockSyncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Live sync (selected SKUs, price + stock together)
  const [liveSyncing, setLiveSyncing] = useState<Set<string>>(new Set());

  // ── Load stores ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStores, {}, token);
      if (resp && !resp.error) setStores(resp);
    })();
  }, [token]);

  // ── Load settings ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getPricingSettings, {}, token);
      if (resp && !resp.error && resp.psychPricingPool) {
        setSettings(resp);
      }
    })();
  }, [token]);

  // ── Fetch opportunities ──────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const data = await dispatch(actions.getPricingOpportunities, { storeId }, token);
      if (data && !data.error) {
        setSkus(data as PricingSkuData[]);
        setLastUpdated(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Sync status (load once, then poll while running) ──────────────────────
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getSyncStatus, {}, token);
      if (resp && !resp.error) setSyncStatus(resp);
    })();
  }, [token]);

  const startSyncPolling = useCallback(() => {
    if (syncPollRef.current) return;
    syncPollRef.current = setInterval(async () => {
      const resp = await dispatch(actions.getSyncStatus, {}, token);
      if (resp && !resp.error) {
        setSyncStatus(resp);
        if (!resp.running) {
          clearInterval(syncPollRef.current!);
          syncPollRef.current = null;
          setSyncing(false);
          // Don't reload — prices flow in via livePrices on next "Fetch live" click
          // User can manually hit Refresh if they want to reload all data
        }
      }
    }, 2500);
  }, [token, fetchData]);

  useEffect(() => () => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
  }, []);

  // ── Load initial stock sync status ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStockSyncStatus, {}, token);
      if (resp && !resp.error) setStockSyncStatus(resp);
    })();
  }, [token]);

  const startStockSyncPolling = useCallback(() => {
    if (stockSyncPollRef.current) return;
    stockSyncPollRef.current = setInterval(async () => {
      const resp = await dispatch(actions.getStockSyncStatus, {}, token);
      if (resp && !resp.error) {
        setStockSyncStatus(resp);
        if (!resp.running) {
          clearInterval(stockSyncPollRef.current!);
          stockSyncPollRef.current = null;
          setSyncingStock(false);
          // Reload opportunities to pick up refreshed jumiaQuantity values
          fetchData();
        }
      }
    }, 3000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => {
    if (stockSyncPollRef.current) clearInterval(stockSyncPollRef.current);
  }, []);

  // ── Fetch live Jumia price for a single SKU ──────────────────────────────────
  const fetchLivePrice = useCallback(async (sku: PricingSkuData) => {
    if (!sku.storeId) return;
    setFetchingPrice(prev => new Set(prev).add(sku._id));
    try {
      const resp = await dispatch(actions.getJumiaPrice, {}, token, { storeId: sku.storeId, sku: sku.sku });
      if (resp && !resp.error) {
        const price = resp.salePrice > 0 ? resp.salePrice : resp.globalPrice;
        if (price) setLivePrices(prev => ({ ...prev, [sku._id]: price }));
      }
    } finally {
      setFetchingPrice(prev => { const s = new Set(prev); s.delete(sku._id); return s; });
    }
  }, [token]);

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  // ── Groups for filter dropdown ────────────────────────────────────────────────
  const allGroups = useMemo(() => {
    const seen = new Map<string, string>();
    skus.forEach(s => seen.set(s.productGroupId, s.productGroupName));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [skus]);

  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [groupSearch,       setGroupSearch]       = useState('');
  const groupDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!groupDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target as Node))
        setGroupDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [groupDropdownOpen]);

  // ── KPIs ─────────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total        = skus.length;
    const noOrders     = skus.filter(s => s.opportunityType === 'no-orders' || s.opportunityType === 'high-price').length;
    const highPrice    = skus.filter(s => s.opportunityType === 'high-price').length;
    const thinMargin   = skus.filter(s => s.opportunityType === 'thin-margin').length;
    const repriceOpps  = skus.filter(s => s.opportunityType === 'reprice-opportunity').length;
    const missingData  = skus.filter(s => s.missingCost || s.missingPrice).length;
    const withSuggestion = skus.filter(s => s.suggestedPrice && s.suggestedPrice !== s.currentPrice).length;
    return { total, noOrders, highPrice, thinMargin, repriceOpps, missingData, withSuggestion };
  }, [skus]);

  // ── Tab counts ───────────────────────────────────────────────────────────────
  const tabCounts = useMemo(() => ({
    all:       skus.length,
    'no-orders': skus.filter(s => s.opportunityType === 'no-orders').length,
    'high-price': skus.filter(s => s.opportunityType === 'high-price').length,
    'thin-margin': skus.filter(s => s.opportunityType === 'thin-margin').length,
    reprice:   skus.filter(s => s.opportunityType === 'reprice-opportunity').length,
    healthy:   skus.filter(s => s.opportunityType === 'healthy').length,
  }), [skus]);

  // ── Merge live prices into skus ───────────────────────────────────────────────
  const enrichedSkus = useMemo<PricingSkuData[]>(() => {
    if (Object.keys(livePrices).length === 0) return skus;
    return skus.map(s => {
      const lp = livePrices[s._id];
      if (!lp || lp === s.currentPrice) return s;
      const commRate   = (s.commissionPct || 15) / 100;
      const commission = lp * commRate;
      const totalCost  = s.totalFixedCosts + commission;
      const profit     = lp - totalCost;
      const marginPct  = lp > 0 ? (profit / lp) * 100 : 0;
      return {
        ...s,
        currentPrice:     lp,
        currentCommission: commission,
        currentTotalCost:  totalCost,
        currentProfit:     profit,
        currentMarginPct:  marginPct,
        missingPrice:      false,
      };
    });
  }, [skus, livePrices]);

  // ── Filtered + sorted list ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = [...enrichedSkus];

    // Tab filter
    if (activeTab === 'no-orders')    r = r.filter(s => s.opportunityType === 'no-orders');
    if (activeTab === 'high-price')   r = r.filter(s => s.opportunityType === 'high-price');
    if (activeTab === 'thin-margin')  r = r.filter(s => s.opportunityType === 'thin-margin');
    if (activeTab === 'reprice')      r = r.filter(s => s.opportunityType === 'reprice-opportunity');
    if (activeTab === 'healthy')      r = r.filter(s => s.opportunityType === 'healthy');

    // Search
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(s =>
        (s.sku || '').toLowerCase().includes(q) ||
        (s.productName || '').toLowerCase().includes(q) ||
        (s.productGroupName || '').toLowerCase().includes(q)
      );
    }

    // Group filter
    if (filterGroup !== 'all') r = r.filter(s => s.productGroupId === filterGroup);

    // Risk filter
    if (filterRisk === 'safe')    r = r.filter(s => s.riskLevel === 'safe');
    if (filterRisk === 'risky')   r = r.filter(s => s.riskLevel === 'risky');
    if (filterRisk === 'missing') r = r.filter(s => s.riskLevel === 'missing-data');

    // Live/deactivated filter
    const isDeact = (s: PricingSkuData) => !!((s.listingStatus && s.listingStatus !== 'ACTIVE') || (s.qcStatus && /REJECT/i.test(s.qcStatus)));
    if (filterVisible === 'live')        r = r.filter(s => s.visible === 1 && !isDeact(s));
    if (filterVisible === 'hidden')      r = r.filter(s => s.visible === 0 && !isDeact(s));
    if (filterVisible === 'deactivated') r = r.filter(isDeact);

    // Sort
    r.sort((a, b) => {
      let av = 0, bv = 0;
      switch (sortBy) {
        case 'daysWithout':  av = a.daysWithoutOrder;   bv = b.daysWithoutOrder;   break;
        case 'currentPrice': av = a.currentPrice ?? -1; bv = b.currentPrice ?? -1; break;
        case 'profit':       av = a.currentProfit ?? -99999; bv = b.currentProfit ?? -99999; break;
        case 'margin':       av = a.currentMarginPct ?? -999; bv = b.currentMarginPct ?? -999; break;
        case 'orders30':     av = a.orders30d;          bv = b.orders30d;          break;
        case 'orders7':      av = a.orders7d;           bv = b.orders7d;           break;
        case 'sugPrice':     av = a.suggestedPrice ?? 0; bv = b.suggestedPrice ?? 0; break;
        case 'diff':         av = a.priceChangeDiff ?? 0; bv = b.priceChangeDiff ?? 0; break;
        case 'stock':        av = (liveStock[a._id] ?? a.jumiaQuantity) ?? -1; bv = (liveStock[b._id] ?? b.jumiaQuantity) ?? -1; break;
        case 'manualStock':  av = a.manualStock ?? -1; bv = b.manualStock ?? -1; break;
        default: break;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return r;
  }, [enrichedSkus, activeTab, search, filterGroup, filterRisk, filterVisible, sortBy, sortDir, liveStock]);

  // ── Pagination ────────────────────────────────────────────────────────────────
  // Rendering all rows at once (the list can be 10k+ SKUs) freezes the browser, so
  // we only mount one page of rows. "Select all" still operates on the full filtered set.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Reset to the first page whenever the filtered set changes (filters/search/sort/tab).
  useEffect(() => { setPage(0); }, [activeTab, search, filterGroup, filterRisk, filterVisible, sortBy, sortDir]);

  // Clamp page if the list shrank below the current offset.
  useEffect(() => { if (page > totalPages - 1) setPage(totalPages - 1); }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page],
  );

  // ── Product Groups tab data ───────────────────────────────────────────────────
  const groupedView = useMemo(() => {
    if (activeTab !== 'groups') return null;
    const map = new Map<string, { name: string; skus: PricingSkuData[] }>();
    for (const s of enrichedSkus) {
      if (!map.has(s.productGroupId)) {
        map.set(s.productGroupId, { name: s.productGroupName, skus: [] });
      }
      map.get(s.productGroupId)!.skus.push(s);
    }
    return Array.from(map.values()).filter(g => g.skus.length > 0);
  }, [enrichedSkus, activeTab]);

  // ── Selection helpers ─────────────────────────────────────────────────────────
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s._id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  // ── Apply flow ────────────────────────────────────────────────────────────────
  const openApplyModal = (sku: PricingSkuData) => {
    if (!sku.storeId) return;
    setApplyTarget(sku);
  };

  const handleApplyModalConfirm = (sku: PricingSkuData, finalPrice: number) => {
    setApplyTarget(null);
    setApplyError(null);
    setConfirmItems([{ sku, finalPrice }]);
  };

  const handleBulkApplySuggested = () => {
    const items = filtered
      .filter(s => selected.has(s._id) && s.suggestedPrice && s.storeId)
      .map(s => ({ sku: s, finalPrice: s.suggestedPrice! }));
    if (items.length === 0) return;
    setApplyError(null);
    setConfirmItems(items);
  };

  const handleInlineApply = (sku: PricingSkuData) => {
    if (!inlineEdit) return;
    const price = parseFloat(inlineEdit.price);
    if (!price || price <= 0) return;
    setInlineEdit(null);
    const enrichedSku = enrichedSkus.find(s => s._id === sku._id) || sku;
    setApplyError(null);
    setConfirmItems([{ sku: enrichedSku, finalPrice: price }]);
  };

  const handleBulkCustomConfirm = (price: number) => {
    const items = filtered
      .filter(s => selected.has(s._id) && s.storeId)
      .map(s => ({ sku: s, finalPrice: price }));
    if (items.length === 0) return;
    setShowBulkCustom(false);
    setApplyError(null);
    setConfirmItems(items);
  };

  const executeApply = async () => {
    if (!confirmItems || confirmItems.length === 0) return;
    setApplying(true);
    setApplyError(null);

    try {
      if (confirmItems.length === 1) {
        const { sku, finalPrice } = confirmItems[0];
        const commRate   = (sku.commissionPct || 15) / 100;
        const commAmt    = finalPrice * commRate;
        const newProfit  = finalPrice - sku.totalFixedCosts - commAmt;
        const newMargin  = finalPrice > 0 ? (newProfit / finalPrice) * 100 : 0;

        const resp = await dispatch(actions.applyPrice, {
          skuId:       sku._id,
          storeId:     sku.storeId,
          newPrice:    finalPrice,
          oldPrice:    sku.currentPrice,
          profitBefore: sku.currentProfit,
          profitAfter:  newProfit,
          marginBefore: sku.currentMarginPct,
          marginAfter:  newMargin,
          reason: `Pricing Intelligence: ${sku.reason}`,
        }, token);

        if (resp?.error) throw new Error(resp.error.message || 'Price update failed');
        setSuccessFlash(`✓ Price updated for ${sku.sku} → ${finalPrice} MAD`);
        setTimeout(() => setSuccessFlash(null), 4000);
        // Update price in local state — no full page reload
        setLivePrices(prev => ({ ...prev, [sku._id]: finalPrice }));
      } else {
        const items = confirmItems.map(({ sku, finalPrice }) => {
          const commAmt   = finalPrice * ((sku.commissionPct || 15) / 100);
          const newProfit = finalPrice - sku.totalFixedCosts - commAmt;
          const newMargin = finalPrice > 0 ? (newProfit / finalPrice) * 100 : 0;
          return {
            skuId:       sku._id,
            storeId:     sku.storeId,
            newPrice:    finalPrice,
            oldPrice:    sku.currentPrice,
            profitBefore: sku.currentProfit,
            profitAfter:  newProfit,
            marginBefore: sku.currentMarginPct,
            marginAfter:  newMargin,
          };
        });

        const resp = await dispatch(actions.bulkApplyPrices, {
          items,
          reason: 'Bulk repricing via Pricing Intelligence',
        }, token);

        if (resp?.error) throw new Error(resp.error.message || 'Bulk apply failed');

        if (resp?.queued) {
          // Background job — poll for status
          const { jobId, total } = resp;
          setSuccessFlash(`⏳ Job queued — applying ${total} prices in background…`);
          const poll = async () => {
            const status = await dispatch(actions.getBulkApplyStatus, {}, token, { jobId });
            if (status?.done || status?.unknown) {
              const applied = status.applied ?? 0;
              const failed  = status.failed  ?? 0;
              setSuccessFlash(`✓ Done — ${applied} applied${failed > 0 ? ` · ${failed} failed` : ''}`);
              setTimeout(() => setSuccessFlash(null), 6000);
              const newPrices: Record<string, number> = {};
              confirmItems.forEach(({ sku, finalPrice }) => { newPrices[sku._id] = finalPrice; });
              setLivePrices(prev => ({ ...prev, ...newPrices }));
            } else {
              const pct = total > 0 ? Math.round(((status.applied + status.failed) / total) * 100) : 0;
              setSuccessFlash(`⏳ Applying… ${status.applied + status.failed}/${total} (${pct}%)`);
              setTimeout(poll, 3000);
            }
          };
          setTimeout(poll, 3000);
        } else {
          const { applied, failed } = resp;
          setSuccessFlash(`✓ Applied ${applied} prices${failed > 0 ? ` · ${failed} failed` : ''}`);
          setTimeout(() => setSuccessFlash(null), 5000);
          const newPrices: Record<string, number> = {};
          confirmItems.forEach(({ sku, finalPrice }) => { newPrices[sku._id] = finalPrice; });
          setLivePrices(prev => ({ ...prev, ...newPrices }));
        }
      }

      setConfirmItems(null);
      clearSelection();
    } catch (err: any) {
      setApplyError(err.message || 'An error occurred');
    } finally {
      setApplying(false);
    }
  };

  // ── Live sync (selected SKUs: price + stock at once) ─────────────────────────
  const handleLiveSync = useCallback(async () => {
    const targets = filtered.filter(s => selected.has(s._id) && s.storeId);
    if (targets.length === 0) return;

    const ids = targets.map(s => s._id);
    setLiveSyncing(new Set(ids));

    try {
      const items = targets.map(s => ({ skuId: s._id, storeId: s.storeId, sku: s.sku }));
      const resp  = await dispatch(actions.liveSyncSkus, { items }, token);

      if (!resp || resp.error) throw new Error(resp?.error?.message || 'Live sync failed');

      const pricePatches: Record<string, number> = {};
      const stockPatches: Record<string, number> = {};
      let   ok = 0, failed = 0;

      for (const r of resp as Array<any>) {
        if (r.success) {
          if (r.price != null) pricePatches[r.skuId] = r.price;
          if (r.stock != null) stockPatches[r.skuId] = r.stock;
          ok++;
        } else {
          failed++;
        }
      }

      if (Object.keys(pricePatches).length) setLivePrices(prev => ({ ...prev, ...pricePatches }));
      if (Object.keys(stockPatches).length) setLiveStock(prev =>  ({ ...prev, ...stockPatches }));

      setSuccessFlash(
        `✓ Synced ${ok} SKU${ok !== 1 ? 's' : ''} — price + stock updated${failed > 0 ? ` · ${failed} failed` : ''}`
      );
      setTimeout(() => setSuccessFlash(null), 5000);
    } catch (err: any) {
      setSuccessFlash(`⚠ Live sync error: ${err.message}`);
      setTimeout(() => setSuccessFlash(null), 5000);
    } finally {
      setLiveSyncing(new Set());
    }
  }, [filtered, selected, token]);

  // ── Add selected SKUs to Auto-Pilot ─────────────────────────────────────────
  const [addingAutoPilot, setAddingAutoPilot] = useState(false);
  const handleAddToAutoPilot = useCallback(async () => {
    const targets = filtered.filter(s => selected.has(s._id) && s.storeId);
    if (targets.length === 0) return;
    const bad = targets.filter(s => s.missingCost || !s.currentPrice);
    if (bad.length && !window.confirm(`${bad.length} of ${targets.length} selected SKUs have no cost or no known price and will be skipped by Auto-Pilot until fixed. Continue?`)) return;
    setAddingAutoPilot(true);
    try {
      const r = await dispatch(actions.autoPilotEnroll, { productIds: targets.map(s => s._id), enabled: true }, token);
      setSuccessFlash(`🤖 Auto-Pilot: ${r.created} enrolled · ${r.updated} already enrolled (re-enabled) — see the Auto-Pilot tab`);
      setTimeout(() => setSuccessFlash(null), 6000);
      setSelected(new Set());
    } catch (err: any) {
      setSuccessFlash(`⚠ Auto-Pilot enroll failed: ${err?.response?.data?.error?.message || err.message}`);
      setTimeout(() => setSuccessFlash(null), 6000);
    } finally { setAddingAutoPilot(false); }
  }, [filtered, selected, token]);

  // ── Stock handlers ────────────────────────────────────────────────────────────
  const handleSyncStock = async () => {
    setSyncingStock(true);
    const resp = await dispatch(actions.syncStock, { storeId }, token);
    if (resp && !resp.error) {
      startStockSyncPolling();
    } else {
      setSyncingStock(false);
    }
  };

  const openStockConfirm = (skus: PricingSkuData[], quantity: number) => {
    setStockError(null);
    setStockConfirmItems({ skus, quantity });
  };

  const executeStockUpdate = async () => {
    if (!stockConfirmItems) return;

    // Capture everything before clearing state
    const targetQty    = stockConfirmItems.quantity;
    const targetSkuIds = stockConfirmItems.skus.map(s => s._id);
    const items = stockConfirmItems.skus.map(s => ({
      skuId:    s._id,
      storeId:  s.storeId,
      sku:      s.sku,
      quantity: targetQty,
    }));

    // Close modal immediately — don't wait for the API
    setStockConfirmItems(null);
    setShowBulkStock(false);
    setBulkStockQty('');
    clearSelection();
    setSuccessFlash(`⏳ Queuing stock update for ${items.length} SKU${items.length !== 1 ? 's' : ''}…`);

    try {
      const resp = await dispatch(actions.bulkUpdateStock, { items }, token);
      if (resp?.error) throw new Error(resp.error.message || 'Stock update failed');

      const { jobId, total } = resp;
      setSuccessFlash(`⏳ Applying… 0/${total} (0%)`);

      const poll = async () => {
        const status = await dispatch(actions.getBulkStockStatus, {}, token, { jobId });
        if (status?.done || status?.unknown) {
          const applied  = status.applied ?? 0;
          const failed   = status.failed  ?? 0;
          const failures: Array<{ skuId: string; sku: string; error: string }> = status.failures ?? [];
          const failedSkuList = failures.map(f => f.sku).slice(0, 5).join(', ');
          setSuccessFlash(
            failed > 0
              ? `⚠ Stock set to ${targetQty} for ${applied} SKU${applied !== 1 ? 's' : ''} · ${failed} REJECTED by Jumia${failedSkuList ? ` (${failedSkuList}${failures.length > 5 ? '…' : ''})` : ''} — their stock did NOT change`
              : `✓ Stock set to ${targetQty} for ${applied} SKU${applied !== 1 ? 's' : ''} — confirmed by Jumia`
          );
          setTimeout(() => setSuccessFlash(null), failed > 0 ? 15000 : 6000);
          // Only reflect the new quantity for SKUs Jumia actually confirmed —
          // patching failed ones would show stock as changed when it wasn't.
          const failedIds = new Set(failures.map(f => f.skuId));
          const patch: Record<string, number> = {};
          for (const id of targetSkuIds) if (!failedIds.has(id)) patch[id] = targetQty;
          setLiveStock(prev => ({ ...prev, ...patch }));
        } else {
          const done = (status.applied ?? 0) + (status.failed ?? 0);
          const pct  = total > 0 ? Math.round((done / total) * 100) : 0;
          setSuccessFlash(`⏳ Applying… ${done}/${total} (${pct}%)`);
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (err: any) {
      setSuccessFlash(`⚠ Stock update failed: ${err.message}`);
      setTimeout(() => setSuccessFlash(null), 6000);
    }
  };

  // ── Sync live prices from Jumia ───────────────────────────────────────────
  const handleSyncPrices = async () => {
    setSyncing(true);
    const resp = await dispatch(actions.syncPrices, { storeId }, token);
    if (resp && !resp.error) {
      startSyncPolling();
    } else {
      setSyncing(false);
    }
  };

  // ── Save settings ────────────────────────────────────────────────────────────
  const handleSaveSettings = async (newSettings: PricingSettings) => {
    setSavingSettings(true);
    const resp = await dispatch(actions.savePricingSettings, newSettings, token);
    if (resp && !resp.error) {
      setSettings(newSettings);
      setShowSettings(false);
      await fetchData();
    }
    setSavingSettings(false);
  };

  // ── Skeleton ──────────────────────────────────────────────────────────────────
  const SkeletonRow = () => (
    <tr>
      {[36, 200, 80, 70, 80, 70, 80, 70, 100, 80, 80].map((w, i) => (
        <td key={i} className="pi__skel-row">
          <div className="pi__skel" style={{ width: w }} />
        </td>
      ))}
    </tr>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="pi">

      {/* ── Top bar ────────────────────────────────────────────────────────────── */}
      <div className="pi__topbar">
        <div className="pi__title-block">
          <div className="pi__title-icon">
            <Tag size={13} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <div className="pi__title">Pricing Intelligence</div>
            <div className="pi__subtitle">
              Margin-aware repricing · psychological prices · floor protection · bulk apply
            </div>
          </div>
        </div>

        {stores.length > 0 && (
          <select
            className="pi__select"
            value={storeId}
            onChange={e => setStoreId(e.target.value)}
          >
            <option value="0">All Stores</option>
            {stores.map((s: IStoreResponse) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        )}

        {lastUpdated && (
          <span className="pi__last-updated">Updated {lastUpdated.toLocaleTimeString()}</span>
        )}

        <button className="pi__settings-btn" onClick={() => setShowSettings(true)}>
          <Settings size={13} /> Settings
        </button>

        <button
          className="pi__settings-btn"
          style={syncing ? { borderColor: '#1D9E75', color: '#1D9E75' } : {}}
          onClick={handleSyncPrices}
          disabled={syncing}
          title="Fetch live prices for all SKUs from Jumia (runs in background)"
        >
          {syncing
            ? <><RefreshCw size={13} className="pi__refresh-spin" /> Syncing prices…</>
            : <><CloudDownload size={13} /> Sync Live Prices</>}
        </button>

        <button
          className="pi__settings-btn"
          style={syncingStock ? { borderColor: '#7C3AED', color: '#7C3AED' } : {}}
          onClick={handleSyncStock}
          disabled={syncingStock}
          title="Fetch allocated stock from Jumia VC for all SKUs (runs in background)"
        >
          {syncingStock
            ? <><RefreshCw size={13} className="pi__refresh-spin" /> Syncing stock…</>
            : <><Layers size={13} /> Sync Stock</>}
        </button>

        <button className="pi__refresh-btn" onClick={fetchData} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'pi__refresh-spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="pi__body">

        {/* ── Sync status banner ─────────────────────────────────────────────── */}
        {syncStatus && (
          <div style={{
            background: '#fff',
            border: '0.5px solid #E2E4E9',
            borderRadius: 10,
            padding: '0.875rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap',
          }}>
            {/* Rolling sync indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              {syncStatus.running
                ? <RefreshCw size={13} className="pi__refresh-spin" style={{ color: '#378ADD' }} />
                : <CheckCircle size={13} style={{ color: '#1D9E75' }} />}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1A1D23' }}>
                  {syncStatus.running ? 'Manual sync running…' : 'Rolling price sync active'}
                </div>
                <div style={{ fontSize: '0.68rem', color: '#9CA3AF', marginTop: 1 }}>
                  {syncStatus.running
                    ? `${syncStatus.done} synced this run`
                    : `~${syncStatus.batchSize || 100} SKUs/hour · full catalog every ~${syncStatus.total > 0 ? Math.ceil(syncStatus.total / (syncStatus.batchSize || 100)) : '?'}h`}
                </div>
              </div>
            </div>

            <div style={{ width: 1, height: 32, background: '#E2E4E9', flexShrink: 0 }} />

            {/* Coverage stats */}
            {[
              {
                label: 'Fresh (< 6h)',
                value: syncStatus.fresh6h ?? '—',
                color: '#1D9E75',
                bg: '#F0FDF4',
                pct: syncStatus.total > 0 ? Math.round(((syncStatus.fresh6h ?? 0) / syncStatus.total) * 100) : 0,
              },
              {
                label: 'Fresh (< 24h)',
                value: syncStatus.fresh24h ?? '—',
                color: '#378ADD',
                bg: '#EFF6FF',
                pct: syncStatus.total > 0 ? Math.round(((syncStatus.fresh24h ?? 0) / syncStatus.total) * 100) : 0,
              },
              {
                label: 'In cache',
                value: syncStatus.total ?? '—',
                color: '#6B7280',
                bg: '#F9FAFB',
                pct: 100,
              },
              {
                label: 'Not found',
                value: syncStatus.errored ?? 0,
                color: syncStatus.errored > 0 ? '#EF9F27' : '#9CA3AF',
                bg: syncStatus.errored > 0 ? '#FFFBEB' : '#F9FAFB',
                pct: null,
              },
            ].map(stat => (
              <div key={stat.label} style={{
                display: 'flex', flexDirection: 'column', gap: 2,
                background: stat.bg, borderRadius: 7, padding: '0.4rem 0.75rem',
                minWidth: 90,
              }}>
                <div style={{ fontSize: '0.68rem', color: '#9CA3AF', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: stat.color, lineHeight: 1.2 }}>
                  {stat.value}
                  {stat.pct !== null && stat.pct !== 100 && syncStatus.total > 0 && (
                    <span style={{ fontSize: '0.68rem', fontWeight: 500, marginLeft: 4, opacity: 0.7 }}>
                      {stat.pct}%
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* Manual sync progress (if running) */}
            {syncStatus.running && (
              <>
                <div style={{ width: 1, height: 32, background: '#E2E4E9', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ height: 6, background: '#DBEAFE', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{
                      height: '100%', borderRadius: 3, background: '#378ADD',
                      width: `${syncStatus.total > 0 ? (syncStatus.done / syncStatus.total) * 100 : 5}%`,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#6B7280' }}>
                    {syncStatus.done} / {syncStatus.total} SKUs
                    {syncStatus.lastError && (
                      <span style={{ color: '#EF9F27', marginLeft: 6 }}>
                        {syncStatus.lastError.includes('backing off') ? `⏸ ${syncStatus.lastError}` : `Last: ${syncStatus.lastError}`}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Last synced time */}
            {syncStatus.lastSyncedAt && !syncStatus.running && (
              <div style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#9CA3AF', textAlign: 'right', flexShrink: 0 }}>
                Most recent sync<br />
                <span style={{ fontWeight: 600, color: '#6B7280' }}>
                  {new Date(syncStatus.lastSyncedAt).toLocaleString('fr-MA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ── Stock sync progress banner ─────────────────────────────────────── */}
        {stockSyncStatus?.running && (
          <div style={{
            background: '#F5F3FF', border: '0.5px solid #DDD6FE', borderRadius: 10,
            padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          }}>
            <RefreshCw size={13} className="pi__refresh-spin" style={{ color: '#7C3AED', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5B21B6' }}>Syncing allocated stock…</div>
              <div style={{ fontSize: '0.68rem', color: '#7C3AED', marginTop: 2 }}>
                {stockSyncStatus.done} / {stockSyncStatus.total} SKUs
                {stockSyncStatus.errors > 0 && ` · ${stockSyncStatus.errors} errors`}
                {stockSyncStatus.lastError && ` · ${stockSyncStatus.lastError}`}
              </div>
            </div>
            {stockSyncStatus.total > 0 && (
              <div style={{ width: 160 }}>
                <div style={{ height: 5, background: '#EDE9FE', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3, background: '#7C3AED',
                    width: `${(stockSyncStatus.done / stockSyncStatus.total) * 100}%`,
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Success flash (fixed toast — visible regardless of scroll) ─────── */}
        {successFlash && (
          <div style={{
            position: 'fixed', bottom: '1.75rem', right: '1.75rem', zIndex: 9999,
            background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10,
            padding: '0.75rem 1.25rem', fontSize: '0.82rem', color: '#15803D', fontWeight: 500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxWidth: 380,
          }}>
            {successFlash}
          </div>
        )}

        {/* ── KPI Grid ───────────────────────────────────────────────────────── */}
        <div className="pi__kpi-grid">
          {[
            {
              icon: <BarChart3 size={14} color="#378ADD" />, iconBg: '#EFF6FF',
              label: 'Total SKUs Analyzed',
              value: loading ? '—' : String(kpis.total),
              colorClass: 'pi__kpi-value--blue',
              sub: 'across all product groups',
            },
            {
              icon: <ShoppingCart size={14} color="#E24B4A" />, iconBg: '#FEF2F2',
              label: 'No Orders',
              value: loading ? '—' : String(kpis.noOrders),
              colorClass: kpis.noOrders > 0 ? 'pi__kpi-value--red' : 'pi__kpi-value--green',
              sub: `≥${settings.daysWithoutOrderThreshold} days without order`,
            },
            {
              icon: <AlertTriangle size={14} color="#EF9F27" />, iconBg: '#FFFBEB',
              label: 'High Price / No Orders',
              value: loading ? '—' : String(kpis.highPrice),
              colorClass: kpis.highPrice > 0 ? 'pi__kpi-value--amber' : 'pi__kpi-value--green',
              sub: 'likely overpriced vs market',
            },
            {
              icon: <TrendingDown size={14} color="#7C3AED" />, iconBg: '#F5F3FF',
              label: 'Thin Margin',
              value: loading ? '—' : String(kpis.thinMargin),
              colorClass: kpis.thinMargin > 0 ? 'pi__kpi-value--violet' : 'pi__kpi-value--green',
              sub: 'margin < 12%',
            },
            {
              icon: <Zap size={14} color="#1D9E75" />, iconBg: '#F0FDF4',
              label: 'Reprice Opportunities',
              value: loading ? '—' : String(kpis.withSuggestion),
              colorClass: 'pi__kpi-value--green',
              sub: 'have a different suggested price',
            },
          ].map((k, i) => (
            <div key={i} className="pi__kpi-card">
              <div className="pi__kpi-header">
                <div className="pi__kpi-icon" style={{ background: k.iconBg }}>{k.icon}</div>
                <span className="pi__kpi-label">{k.label}</span>
              </div>
              <div className={`pi__kpi-value ${k.colorClass}`}>{k.value}</div>
              <div className="pi__kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Tab bar ────────────────────────────────────────────────────────── */}
        <div className="pi__tab-bar">
          {([
            ['all',         'All SKUs'],
            ['no-orders',   'No Orders'],
            ['high-price',  'High Price'],
            ['thin-margin', 'Thin Margin'],
            ['reprice',     'Reprice Opp.'],
            ['healthy',     'Healthy'],
            ['groups',      'Product Groups'],
            ['autopilot',   '🤖 Auto-Pilot'],
            ['logs',        'Reprice Logs'],
          ] as [TabKey, string][]).map(([key, label]) => {
            const count = key in tabCounts ? (tabCounts as any)[key] : undefined;
            return (
              <button
                key={key}
                className={`pi__tab${activeTab === key ? ' pi__tab--active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
                {count !== undefined && (
                  <span className="pi__tab-count">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Logs tab ───────────────────────────────────────────────────────── */}
        {activeTab === 'logs' && <LogsTable token={token} />}

        {/* ── Auto-Pilot tab ─────────────────────────────────────────────────── */}
        {activeTab === 'autopilot' && (
          <AutoPilotPanel
            token={token}
            storeId={storeId}
            candidates={enrichedSkus}
            flash={(msg: string) => { setSuccessFlash(msg); setTimeout(() => setSuccessFlash(null), 6000); }}
          />
        )}

        {/* ── Groups tab ────────────────────────────────────────────────────── */}
        {activeTab === 'groups' && groupedView && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {groupedView.map(grp => {
              const grpNoOrders  = grp.skus.filter(s => s.daysWithoutOrder >= settings.daysWithoutOrderThreshold).length;
              const grpThinMargin = grp.skus.filter(s => s.currentMarginPct != null && s.currentMarginPct < 12).length;
              const avgPrice     = grp.skus.filter(s => s.currentPrice).reduce((sum, s, _, arr) => sum + (s.currentPrice! / arr.length), 0);
              return (
                <div key={grp.name} className="pi__group-section">
                  <div className="pi__group-section-header">
                    <span className="pi__group-section-name">{grp.name}</span>
                    <span className="pi__group-section-meta">{grp.skus.length} SKU{grp.skus.length !== 1 ? 's' : ''}</span>
                    {grpNoOrders > 0 && (
                      <span className="pi__badge pi__badge--no-orders">{grpNoOrders} no orders</span>
                    )}
                    {grpThinMargin > 0 && (
                      <span className="pi__badge pi__badge--thin-margin">{grpThinMargin} thin margin</span>
                    )}
                    {avgPrice > 0 && (
                      <span className="pi__group-section-meta">Avg price: {fmtMAD(avgPrice)}</span>
                    )}
                  </div>
                  <table className="pi__table">
                    <thead>
                      <tr>
                        <th>SKU / Name</th>
                        <th>Current Price</th>
                        <th>Profit</th>
                        <th>Margin</th>
                        <th>Orders 30d</th>
                        <th>Days No Order</th>
                        <th>Suggested</th>
                        <th>Diff</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grp.skus.map(s => (
                        <tr key={s._id} className="pi__row">
                          <td>
                            <div className="pi__product-cell">
                              {s.productImage
                                ? <img src={s.productImage} alt="" className="pi__product-img" />
                                : <div className="pi__product-img-placeholder"><Package size={14} color="#D1D5DB" /></div>
                              }
                              <div>
                                {s.productUrl
                                  ? <a href={s.productUrl} target="_blank" rel="noopener noreferrer" className="pi__product-sku pi__sku-link">{s.sku}</a>
                                  : <span className="pi__product-sku">{s.sku}</span>}
                                <span className="pi__product-name" style={{ fontSize: '0.72rem' }}>
                                  {s.productName || '—'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="pi__price-val">
                              {s.currentPrice ? `${s.currentPrice} MAD` : '—'}
                            </span>
                          </td>
                          <td>
                            <span className={`pi__num ${s.currentProfit != null ? (s.currentProfit >= 0 ? 'pi__num--green' : 'pi__num--red') : 'pi__num--muted'}`}>
                              {fmtMAD(s.currentProfit)}
                            </span>
                          </td>
                          <td>
                            <span className={`pi__num ${margCol(s.currentMarginPct)}`}>{fmtPct(s.currentMarginPct)}</span>
                          </td>
                          <td><span className="pi__num">{s.orders30d}</span></td>
                          <td>
                            <span className={`pi__days-val ${s.daysWithoutOrder >= 9999 ? 'pi__days--critical' : daysClass(s.daysWithoutOrder)}`}>
                              {s.daysWithoutOrder >= 9999 ? 'Never' : `${s.daysWithoutOrder}d`}
                            </span>
                          </td>
                          <td>
                            <span className="pi__sugg-price">
                              {s.suggestedPrice ? `${s.suggestedPrice} MAD` : '—'}
                            </span>
                          </td>
                          <td>
                            {s.priceChangeDiff != null && (
                              <span className={`pi__price-diff ${s.priceChangeDiff < 0 ? 'pi__price-diff--down' : s.priceChangeDiff > 0 ? 'pi__price-diff--up' : 'pi__price-diff--flat'}`}>
                                {s.priceChangeDiff > 0 ? '+' : ''}{Math.round(s.priceChangeDiff)}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`pi__badge pi__badge--${s.opportunityType === 'no-orders' ? 'no-orders' : s.opportunityType === 'high-price' ? 'high-price' : s.opportunityType === 'thin-margin' ? 'thin-margin' : 'safe'}`}>
                              {s.opportunityType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Main table (all tabs except groups/logs) ───────────────────────── */}
        {activeTab !== 'groups' && activeTab !== 'logs' && activeTab !== 'autopilot' && (
          <>
            {/* Filter bar */}
            <div className="pi__filter-bar">
              <div className="pi__search">
                <Search size={13} color="#9CA3AF" />
                <input
                  placeholder="Search SKU, product name, group…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="pi__filter-divider" />

              {allGroups.length > 1 && (
                <div className="pi__filter-group" ref={groupDropdownRef} style={{ position: 'relative' }}>
                  <span className="pi__filter-label">Group</span>
                  <button
                    className="pi__filter-select pi__group-trigger"
                    onClick={() => { setGroupDropdownOpen(o => !o); setGroupSearch(''); }}
                  >
                    <span className="pi__group-trigger-label">
                      {filterGroup === 'all' ? 'All Groups' : (allGroups.find(([id]) => id === filterGroup)?.[1] ?? 'All Groups')}
                    </span>
                    {filterGroup !== 'all' && (
                      <X size={11} style={{ flexShrink: 0, opacity: 0.5 }}
                        onClick={e => { e.stopPropagation(); setFilterGroup('all'); setGroupDropdownOpen(false); }} />
                    )}
                    <ChevronDown size={11} style={{ flexShrink: 0 }} />
                  </button>

                  {groupDropdownOpen && (
                    <div className="pi__group-dropdown">
                      <div className="pi__group-search">
                        <Search size={12} color="#9CA3AF" />
                        <input
                          autoFocus
                          placeholder="Search group…"
                          value={groupSearch}
                          onChange={e => setGroupSearch(e.target.value)}
                        />
                        {groupSearch && (
                          <button onClick={() => setGroupSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                            <X size={11} color="#9CA3AF" />
                          </button>
                        )}
                      </div>
                      <div className="pi__group-list">
                        <button
                          className={`pi__group-option${filterGroup === 'all' ? ' pi__group-option--active' : ''}`}
                          onClick={() => { setFilterGroup('all'); setGroupDropdownOpen(false); setGroupSearch(''); }}
                        >
                          All Groups
                          <span className="pi__group-option-count">{skus.length}</span>
                        </button>
                        {allGroups
                          .filter(([, name]) => name.toLowerCase().includes(groupSearch.toLowerCase()))
                          .map(([id, name]) => {
                            const count = enrichedSkus.filter(s => s.productGroupId === id).length;
                            return (
                              <button
                                key={id}
                                className={`pi__group-option${filterGroup === id ? ' pi__group-option--active' : ''}`}
                                onClick={() => { setFilterGroup(id); setGroupDropdownOpen(false); setGroupSearch(''); }}
                              >
                                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                                <span className="pi__group-option-count">{count}</span>
                              </button>
                            );
                          })}
                        {allGroups.filter(([, name]) => name.toLowerCase().includes(groupSearch.toLowerCase())).length === 0 && (
                          <div className="pi__group-no-results">No groups found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pi__filter-group">
                <span className="pi__filter-label">Risk</span>
                <select
                  className="pi__filter-select"
                  value={filterRisk}
                  onChange={e => setFilterRisk(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="safe">Safe</option>
                  <option value="risky">Risky</option>
                  <option value="missing">Missing Data</option>
                </select>
              </div>

              <div className="pi__filter-group">
                <span className="pi__filter-label">Status</span>
                <select
                  className="pi__filter-select"
                  value={filterVisible}
                  onChange={e => setFilterVisible(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="live">Live</option>
                  <option value="hidden">Hidden (active, not visible)</option>
                  <option value="deactivated">Deactivated / QC rejected</option>
                </select>
              </div>

              <div className="pi__filter-divider" />

              {/* Sort buttons */}
              {[
                ['daysWithout',  'Days No Order'],
                ['currentPrice', 'Price'],
                ['profit',       'Profit'],
                ['margin',       'Margin'],
                ['orders30',     'Orders 30d'],
                ['sugPrice',     'Sugg. Price'],
                ['diff',         'Diff'],
              ].map(([f, label]) => (
                <button
                  key={f}
                  className={`pi__action-btn pi__action-btn--ghost${sortBy === f ? ' pi__tab--active' : ''}`}
                  style={{ padding: '0.3rem 0.6rem', fontSize: '0.72rem', border: sortBy === f ? 'none' : undefined }}
                  onClick={() => handleSort(f)}
                >
                  {label}
                  {sortBy === f && (
                    sortDir === 'desc'
                      ? <ChevronDown size={11} />
                      : <ChevronUp size={11} />
                  )}
                </button>
              ))}

              <span className="pi__count-badge">{filtered.length} SKUs</span>
            </div>

            {/* Bulk selection bar */}
            {selected.size > 0 && (
              <div className="pi__bulk-bar">
                <span className="pi__bulk-info">
                  {selected.size} SKU{selected.size !== 1 ? 's' : ''} selected
                  {' · '}
                  {filtered.filter(s => selected.has(s._id) && s.suggestedPrice && s.suggestedPrice !== s.currentPrice).length} have a suggested price
                </span>
                <button
                  className="pi__bulk-action-btn"
                  style={{ background: '#1D9E75' }}
                  onClick={handleLiveSync}
                  disabled={liveSyncing.size > 0}
                  title="Fetch live price + allocated stock from Jumia for selected SKUs"
                >
                  {liveSyncing.size > 0
                    ? <><RefreshCw size={13} className="pi__refresh-spin" /> Syncing {liveSyncing.size}…</>
                    : <><RefreshCw size={13} /> Sync Live</>}
                </button>
                <button className="pi__bulk-action-btn" onClick={handleBulkApplySuggested}>
                  <Zap size={13} /> Apply Suggested Prices
                </button>
                <button
                  className="pi__bulk-action-btn"
                  style={{ background: '#378ADD' }}
                  onClick={() => setShowBulkCustom(true)}
                >
                  <Tag size={13} /> Set Custom Price…
                </button>
                <button
                  className="pi__bulk-action-btn"
                  style={{ background: '#7C3AED' }}
                  onClick={() => setShowBulkStock(true)}
                >
                  <Layers size={13} /> Set Stock…
                </button>
                <button
                  className="pi__bulk-action-btn"
                  style={{ background: '#0F766E' }}
                  onClick={handleAddToAutoPilot}
                  disabled={addingAutoPilot}
                  title="Enroll selected SKUs in Auto-Pilot sweet-spot pricing (needs cost + price)"
                >
                  <Bot size={13} /> {addingAutoPilot ? 'Adding…' : 'Add to Auto-Pilot'}
                </button>
                <button
                  className="pi__bulk-action-btn"
                  style={{ background: '#6B7280' }}
                  onClick={() => openStockConfirm(filtered.filter(s => selected.has(s._id) && s.storeId), 0)}
                  title="Set stock to 0 (hides from Jumia search)"
                >
                  <X size={13} /> Zero Stock
                </button>
                <button className="pi__bulk-clear-btn" onClick={clearSelection}>
                  Clear selection
                </button>
              </div>
            )}

            {/* Main table */}
            <div className="pi__table-wrap">
              <table className="pi__table">
                <thead>
                  <tr>
                    <th className="pi__check-cell">
                      <input
                        type="checkbox"
                        className="pi__checkbox"
                        checked={!loading && filtered.length > 0 && selected.size === filtered.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <SortTh field="name_" label="Product / SKU" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th>Group</th>
                    <SortTh field="currentPrice" label="Current Price" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th>Cost Breakdown</th>
                    <SortTh field="profit" label="Profit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="margin" label="Margin" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="daysWithout" label="Last Order" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="orders30" label="Orders" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="manualStock" label="🏬 Warehouse Stock" accent="pi__th--warehouse" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="stock" label="🛒 Jumia Stock" accent="pi__th--jumia" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortTh field="sugPrice" label="Suggested Price" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th>Reason / Badges</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                    : filtered.length === 0
                    ? (
                      <tr>
                        <td colSpan={13}>
                          <div className="pi__empty">
                            <div className="pi__empty-icon">🏷️</div>
                            <div className="pi__empty-msg">No SKUs match your current filters</div>
                          </div>
                        </td>
                      </tr>
                    )
                    : paged.map(s => {
                        const isSelected   = selected.has(s._id);
                        const isFetching   = fetchingPrice.has(s._id);
                        const isSyncing    = liveSyncing.has(s._id);
                        const livePrice    = livePrices[s._id];
                        const displayPrice = livePrice || s.currentPrice;
                        const diff         = s.priceChangeDiff;

                        return (
                          <tr
                            key={s._id}
                            className={`pi__row${isSelected ? ' pi__row--selected' : ''}${s.isProtected ? ' pi__row--protected' : ''}${isSyncing ? ' pi__row--syncing' : ''}`}
                          >
                            {/* Checkbox */}
                            <td className="pi__check-cell">
                              <input
                                type="checkbox"
                                className="pi__checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(s._id)}
                              />
                            </td>

                            {/* Product */}
                            <td>
                              <div className="pi__product-cell">
                                {s.productImage
                                  ? <img src={s.productImage} alt="" className="pi__product-img" />
                                  : <div className="pi__product-img-placeholder"><Package size={14} color="#D1D5DB" /></div>
                                }
                                <div>
                                  <span className="pi__product-name">{s.productName || s.sku}</span>
                                  {s.productUrl
                                    ? <a href={s.productUrl} target="_blank" rel="noopener noreferrer" className="pi__product-sku pi__sku-link">{s.sku}</a>
                                    : <span className="pi__product-sku">{s.sku}</span>}
                                  {s.storeName && <span className="pi__product-group">{s.storeName}</span>}
                                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                                    {s.listingStatus && s.listingStatus !== 'ACTIVE' ? (
                                      <span className="pi__badge pi__badge--risky" title={`Jumia listing status: ${s.listingStatus}`}>Deactivated · {s.listingStatus}</span>
                                    ) : s.qcStatus && /REJECT/i.test(s.qcStatus) ? (
                                      <span className="pi__badge pi__badge--risky" title="Rejected by Jumia quality control">QC rejected</span>
                                    ) : s.visible === 0 ? (
                                      <span className="pi__badge pi__badge--no-orders" title={`Listing is ACTIVE but not visible on the site (usually stock 0)${s.visibleUpdatedAt ? ` — visibility changed ${new Date(s.visibleUpdatedAt).toLocaleString()}` : ''}`}>Hidden</span>
                                    ) : s.visible === 1 ? (
                                      <span className="pi__badge pi__badge--safe" title={s.visibleUpdatedAt ? `Visible since ${new Date(s.visibleUpdatedAt).toLocaleString()}` : 'Visible on Jumia'}>Live</span>
                                    ) : null}
                                    {s.isProtected && (
                                      <span className="pi__badge pi__badge--protected">Protected</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Group */}
                            <td>
                              <span style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                                {s.productGroupName || '—'}
                              </span>
                            </td>

                            {/* Current price — inline-editable */}
                            <td>
                              {inlineEdit?.id === s._id ? (
                                /* ── Inline editor active ── */
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    autoFocus
                                    className="pi__inline-input"
                                    value={inlineEdit.price}
                                    onChange={e => setInlineEdit({ id: s._id, price: e.target.value })}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter')  handleInlineApply(s);
                                      if (e.key === 'Escape') setInlineEdit(null);
                                    }}
                                  />
                                  <button
                                    className="pi__inline-confirm"
                                    onClick={() => handleInlineApply(s)}
                                    title="Confirm — opens preview"
                                  >
                                    <Check size={11} />
                                  </button>
                                  <button
                                    className="pi__inline-cancel"
                                    onClick={() => setInlineEdit(null)}
                                    title="Cancel"
                                  >
                                    <X size={11} />
                                  </button>
                                </div>
                              ) : displayPrice ? (
                                /* ── Price known — show with edit hint ── */
                                <div
                                  className="pi__price-editable"
                                  onClick={() => !s.isProtected && setInlineEdit({ id: s._id, price: String(displayPrice) })}
                                  title={s.isProtected ? undefined : 'Click to change price'}
                                >
                                  <span className="pi__price-val">{displayPrice} MAD</span>
                                  {!s.isProtected && (
                                    <span className="pi__price-edit-hint">
                                      <Pencil size={9} />
                                    </span>
                                  )}
                                  <span className="pi__price-sub" style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                    {s.priceSource === 'cache'
                                      ? <><CheckCircle size={9} style={{ color: '#1D9E75' }} /><span style={{ color: '#1D9E75' }}>Live Jumia</span></>
                                      : s.priceSource === 'order-estimated'
                                      ? <span style={{ color: '#EF9F27' }} title="Estimated from order payout — sync live prices for accuracy">⚡ ~Est. from payout</span>
                                      : livePrice && !s.currentPrice
                                      ? <><CheckCircle size={9} style={{ color: '#1D9E75' }} /><span style={{ color: '#1D9E75' }}>Fetched</span></>
                                      : null}
                                  </span>
                                  <span className="pi__price-sub">Floor: {s.floorPrice} MAD</span>
                                </div>
                              ) : (
                                /* ── No price yet ── */
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <button
                                    className="pi__inline-manual"
                                    onClick={() => !s.isProtected && setInlineEdit({ id: s._id, price: '' })}
                                    title="Type a price manually"
                                  >
                                    <Pencil size={9} /> Type price
                                  </button>
                                  <button
                                    className="pi__fetch-btn"
                                    disabled={isFetching || !s.storeId}
                                    onClick={() => fetchLivePrice(s)}
                                  >
                                    {isFetching
                                      ? <><RefreshCw size={9} className="pi__refresh-spin" /> Fetching…</>
                                      : <><Tag size={9} /> Fetch live</>}
                                  </button>
                                </div>
                              )}
                            </td>

                            {/* Cost breakdown */}
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.68rem', color: '#6B7280' }}>
                                <span>Buying: {fmtMAD(s.buyingCost)}</span>
                                {s.commissionPct > 0 && (
                                  <span>Commission: {s.commissionPct}%{displayPrice ? ` (${fmtMAD(displayPrice * s.commissionPct / 100)})` : ''}</span>
                                )}
                                <span>Frais: {fmtMAD(s.fraisTraitement)}</span>
                                {(s.transportFees + s.storageFees + s.packageFees) > 0 && (
                                  <span>Other: {fmtMAD(s.transportFees + s.storageFees + s.packageFees)}</span>
                                )}
                                {s.missingCost && (
                                  <span style={{ color: '#EF9F27', fontWeight: 600 }}>⚠ Cost missing</span>
                                )}
                              </div>
                            </td>

                            {/* Profit */}
                            <td>
                              <span className={`pi__num${s.currentProfit != null ? (s.currentProfit > 0 ? ' pi__num--green' : ' pi__num--red') : ' pi__num--muted'}`}>
                                {fmtMAD(s.currentProfit)}
                              </span>
                              {s.currentProfit != null && (
                                <span className="pi__num-sub">per unit</span>
                              )}
                            </td>

                            {/* Margin */}
                            <td>
                              <div className="pi__margin-bar">
                                <span className={`pi__num ${margCol(s.currentMarginPct)}`} style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                  {fmtPct(s.currentMarginPct)}
                                </span>
                                {s.currentMarginPct != null && (
                                  <div className="pi__margin-track">
                                    <div
                                      className="pi__margin-fill"
                                      style={{
                                        width: `${clamp(s.currentMarginPct, 0, 100)}%`,
                                        background: s.currentMarginPct >= 20 ? '#1D9E75' : s.currentMarginPct >= 8 ? '#EF9F27' : '#E24B4A',
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Last order / days */}
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span className={`pi__days-val ${s.daysWithoutOrder >= 9999 ? 'pi__days--critical' : daysClass(s.daysWithoutOrder)}`}>
                                  {s.daysWithoutOrder >= 9999 ? 'Never sold' : `${s.daysWithoutOrder}d ago`}
                                </span>
                                {s.lastOrderDate && (
                                  <span className="pi__price-sub">
                                    {new Date(s.lastOrderDate).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short' })}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Orders */}
                            <td>
                              <span className="pi__num">{s.orders30d}</span>
                              <span className="pi__num-sub">{s.orders7d} this week</span>
                            </td>

                            {/* Warehouse Stock (manual) */}
                            <td>
                              <div className="pi__stock-cell pi__stock-cell--warehouse">
                                <Package size={12} className="pi__stock-icon pi__stock-icon--warehouse" />
                                {s.manualStock != null
                                  ? <span className={`pi__num${s.manualStock === 0 ? ' pi__num--red' : s.manualStock <= 5 ? ' pi__num--amber' : ' pi__num--green'}`}
                                      style={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                      {s.manualStock}
                                    </span>
                                  : <span className="pi__missing-val">—</span>
                                }
                              </div>
                            </td>

                            {/* Jumia Stock (allocated on Vendor Center) */}
                            {(() => {
                              const displayQty = liveStock[s._id] ?? s.jumiaQuantity;
                              const isEditing  = inlineStockEdit?.id === s._id;
                              return (
                                <td>
                                  <div className="pi__stock-cell pi__stock-cell--jumia">
                                  <ShoppingCart size={12} className="pi__stock-icon pi__stock-icon--jumia" />
                                  {isEditing ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        autoFocus
                                        className="pi__inline-input"
                                        style={{ width: 64 }}
                                        value={inlineStockEdit.qty}
                                        onChange={e => setInlineStockEdit({ id: s._id, qty: e.target.value })}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            const q = parseInt(inlineStockEdit.qty, 10);
                                            if (!isNaN(q) && q >= 0) {
                                              setInlineStockEdit(null);
                                              openStockConfirm([s], q);
                                            }
                                          }
                                          if (e.key === 'Escape') setInlineStockEdit(null);
                                        }}
                                      />
                                      <button className="pi__inline-confirm"
                                        onClick={() => {
                                          const q = parseInt(inlineStockEdit.qty, 10);
                                          if (!isNaN(q) && q >= 0) { setInlineStockEdit(null); openStockConfirm([s], q); }
                                        }}>
                                        <Check size={11} />
                                      </button>
                                      <button className="pi__inline-cancel" onClick={() => setInlineStockEdit(null)}>
                                        <X size={11} />
                                      </button>
                                    </div>
                                  ) : displayQty != null ? (
                                    <div
                                      className="pi__price-editable"
                                      onClick={() => setInlineStockEdit({ id: s._id, qty: String(displayQty) })}
                                      title="Click to update allocated stock"
                                    >
                                      <span className={`pi__num${displayQty === 0 ? ' pi__num--red' : displayQty <= 5 ? ' pi__num--amber' : ' pi__num--green'}`}
                                        style={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                        {displayQty}
                                      </span>
                                      <span className="pi__price-edit-hint"><Pencil size={9} /></span>
                                      {s.jumiaQuantitySyncedAt && (
                                        <span className="pi__price-sub" style={{ marginTop: 2 }}>
                                          {new Date(s.jumiaQuantitySyncedAt).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short' })}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span
                                      className="pi__missing-val"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setInlineStockEdit({ id: s._id, qty: '' })}
                                      title="Click to set stock manually"
                                    >
                                      — set
                                    </span>
                                  )}
                                  </div>
                                </td>
                              );
                            })()}

                            {/* Suggested price */}
                            <td>
                              {s.suggestedPrice ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  <span className="pi__sugg-price">{s.suggestedPrice} MAD</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                    {diff != null && (
                                      <span className={`pi__price-diff ${diff < 0 ? 'pi__price-diff--down' : diff > 0 ? 'pi__price-diff--up' : 'pi__price-diff--flat'}`}>
                                        {diff > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                                        {diff > 0 ? '+' : ''}{Math.round(diff)} MAD
                                      </span>
                                    )}
                                  </div>
                                  {s.suggestedProfit != null && (
                                    <span className={`pi__price-sub${s.suggestedProfit > 0 ? ' pi__num--green' : ' pi__num--red'}`}>
                                      {fmtMAD(s.suggestedProfit)} profit
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="pi__missing-val">
                                  {s.missingCost ? 'Missing cost data' : s.missingPrice ? 'Fetch price first' : '—'}
                                </span>
                              )}
                            </td>

                            {/* Reason + badges */}
                            <td>
                              <div className="pi__badges" style={{ marginBottom: '0.25rem' }}>
                                {s.badges.map(b => (
                                  <span key={b} className={`pi__badge ${badgeClass(b)}`}>{b}</span>
                                ))}
                              </div>
                              <div className="pi__reason">{s.reason}</div>
                            </td>

                            {/* Action */}
                            <td>
                              {s.isProtected ? (
                                <span style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>Protected</span>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {/* Always show Set Price */}
                                  <button
                                    className="pi__action-btn pi__action-btn--apply"
                                    disabled={!s.storeId}
                                    onClick={() => openApplyModal(s)}
                                    title={!s.storeId ? 'Store not identified' : 'Set a new price for this SKU'}
                                    style={{ justifyContent: 'center' }}
                                  >
                                    <TrendingUp size={11} /> Set Price
                                  </button>
                                  {/* Show Get Price when no live price is cached */}
                                  {(s.missingPrice && !displayPrice) && (
                                    <button
                                      className="pi__action-btn pi__action-btn--fetch"
                                      disabled={isFetching || !s.storeId}
                                      onClick={() => fetchLivePrice(s)}
                                      style={{ justifyContent: 'center' }}
                                    >
                                      {isFetching
                                        ? <><RefreshCw size={11} className="pi__refresh-spin" /> Fetching…</>
                                        : <><Tag size={11} /> Get Price</>}
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filtered.length > PAGE_SIZE && (
              <div className="pi__pagination">
                <span className="pi__pagination-info">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} SKUs
                </span>
                <div className="pi__pagination-controls">
                  <button
                    className="pi__action-btn pi__action-btn--ghost"
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                  >« First</button>
                  <button
                    className="pi__action-btn pi__action-btn--ghost"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >‹ Prev</button>
                  <span className="pi__pagination-page">Page {page + 1} / {totalPages}</span>
                  <button
                    className="pi__action-btn pi__action-btn--ghost"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >Next ›</button>
                  <button
                    className="pi__action-btn pi__action-btn--ghost"
                    onClick={() => setPage(totalPages - 1)}
                    disabled={page >= totalPages - 1}
                  >Last »</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Bulk stock input ──────────────────────────────────────────────── */}
      {showBulkStock && (
        <div className="pi__modal-overlay" onClick={e => { if (e.target === e.currentTarget) { setShowBulkStock(false); setBulkStockQty(''); } }}>
          <div className="pi__modal">
            <div className="pi__modal-header">
              <div className="pi__modal-title">Set Stock — {filtered.filter(s => selected.has(s._id) && s.storeId).length} SKUs</div>
              <button className="pi__modal-close" onClick={() => { setShowBulkStock(false); setBulkStockQty(''); }}><X size={14} /></button>
            </div>
            <div className="pi__modal-body">
              <div style={{ padding: '0.5rem 0' }}>
                <div className="pi__section-label">New Allocated Stock Quantity</div>
                <div className="pi__custom-price-row" style={{ marginTop: '0.5rem' }}>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    autoFocus
                    className="pi__price-input"
                    placeholder="e.g. 100"
                    value={bulkStockQty}
                    onChange={e => setBulkStockQty(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const q = parseInt(bulkStockQty, 10);
                        if (!isNaN(q) && q >= 0) openStockConfirm(filtered.filter(s => selected.has(s._id) && s.storeId), q);
                      }
                    }}
                  />
                </div>
                {parseInt(bulkStockQty, 10) === 0 && (
                  <div className="pi__warning-box" style={{ marginTop: '0.5rem' }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    Setting stock to 0 hides products from Jumia search.
                  </div>
                )}
              </div>
            </div>
            <div className="pi__modal-footer">
              <button className="pi__action-btn pi__action-btn--ghost" onClick={() => { setShowBulkStock(false); setBulkStockQty(''); }}>Cancel</button>
              <button
                className="pi__action-btn pi__action-btn--apply"
                style={{ background: '#7C3AED' }}
                disabled={!bulkStockQty || isNaN(parseInt(bulkStockQty, 10)) || parseInt(bulkStockQty, 10) < 0}
                onClick={() => {
                  const q = parseInt(bulkStockQty, 10);
                  if (!isNaN(q) && q >= 0) openStockConfirm(filtered.filter(s => selected.has(s._id) && s.storeId), q);
                }}
              >
                <Check size={12} /> Next: Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stock confirm modal ────────────────────────────────────────────── */}
      {stockConfirmItems && (
        <StockConfirmModal
          skus={stockConfirmItems.skus}
          quantity={stockConfirmItems.quantity}
          onClose={() => { setStockConfirmItems(null); setStockError(null); }}
          onConfirm={executeStockUpdate}
          applying={applyingStock}
          error={stockError}
        />
      )}

      {/* ── Bulk custom price modal ────────────────────────────────────────── */}
      {showBulkCustom && (
        <BulkCustomPriceModal
          selectedSkus={filtered.filter(s => selected.has(s._id) && s.storeId)}
          psychPool={settings.psychPricingPool || DEFAULT_PSYCH_POOL}
          onClose={() => setShowBulkCustom(false)}
          onApply={handleBulkCustomConfirm}
        />
      )}

      {/* ── Apply (set price) modal ─────────────────────────────────────────── */}
      {applyTarget && (
        <ApplyModal
          sku={enrichedSkus.find(s => s._id === applyTarget._id) || applyTarget}
          psychPool={settings.psychPricingPool || DEFAULT_PSYCH_POOL}
          onClose={() => setApplyTarget(null)}
          onApply={handleApplyModalConfirm}
        />
      )}

      {/* ── Confirm modal ──────────────────────────────────────────────────── */}
      {confirmItems && (
        <ConfirmModal
          items={confirmItems}
          onClose={() => { setConfirmItems(null); setApplyError(null); }}
          onConfirm={executeApply}
          applying={applying}
          error={applyError}
        />
      )}

      {/* ── Settings panel ─────────────────────────────────────────────────── */}
      {showSettings && (
        <SettingsPanel
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveSettings}
          saving={savingSettings}
        />
      )}
    </div>
  );
};

export default PricingIntelligencePage;
